// ticket-status-pie-chart.tsx — Ticket status distribution donut.
import { PieChart } from '@vnetwork/ui/charts';
import { Card, CardBody, CardTitle, Skeleton } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { PieChart as PieIcon } from 'lucide-react';
import type { TicketStatusSummary } from '@/api/tickets-statistics';

const STATUS_COLOR: Record<string, string> = {
  opening: 'var(--vn-info-solid)',
  'work-in-progress': 'var(--vn-warning-solid)',
  escalated: 'var(--vn-danger-solid)',
  closed: 'var(--vn-fg-subtle)',
  completed: 'var(--vn-success-solid)',
};

interface Props {
  data: TicketStatusSummary[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function TicketStatusPieChart({ data, isLoading, isError }: Props) {
  const slices = (data ?? [])
    .filter((d) => d.total_tickets > 0)
    .map((d) => ({
      name: d.ticket_status.replace(/-/g, ' '),
      value: d.total_tickets,
      color: STATUS_COLOR[d.ticket_status] ?? 'var(--vn-border-default)',
    }));

  const total = slices.reduce((s, x) => s + x.value, 0);

  return (
    <Card flat>
      <CardBody>
        <CardTitle style={{ marginBottom: 'var(--vn-space-3)' }}>Status Distribution</CardTitle>
        {isError ? (
          <Alert tone="danger" title="Failed to load status summary" />
        ) : isLoading ? (
          <Skeleton style={{ height: 220 }} />
        ) : slices.length === 0 ? (
          <EmptyState icon={<PieIcon size={20} />} title="No data" description="No tickets in this date range." />
        ) : (
          <PieChart
            donut
            height={220}
            data={slices}
            showLegend
            valueFormatter={(v) => `${v ?? 0} tickets`}
            centerLabel={
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 'var(--vn-font-size-xl)', fontWeight: 'var(--vn-font-weight-semibold)', color: 'var(--vn-fg-default)' }}>
                  {total.toLocaleString('en-US')}
                </div>
                <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>tickets</div>
              </div>
            }
          />
        )}
      </CardBody>
    </Card>
  );
}
